import { Tag } from '@/components';
import { CommentPublishTypeLabelModifier } from '@/features/comment/models/comment';
import { type FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type CommentFilterParams,
  CommentResolvedStatusLabelModifier,
} from '../../../models/search';

type CommentFilterParamsKey = keyof CommentFilterParams;
type CommentFilterParamsValue = CommentFilterParams[CommentFilterParamsKey];

const FILTER_KEYS = [
  'readStatus',
  'publishType',
  'resolved',
  'isCurrentActiveTabOnly',
] satisfies CommentFilterParamsKey[];

export const CommentFilterTag: FC<{
  filterKey: CommentFilterParamsKey;
  value: CommentFilterParamsValue;
  onRemoveFilter: (key: CommentFilterParamsKey) => void;
}> = ({ filterKey, value, onRemoveFilter }) => {
  const { t } = useTranslation();

  const getFilterLabel = useCallback(
    (key: CommentFilterParamsKey, value: CommentFilterParamsValue) => {
      switch (key) {
        case 'publishType':
          return typeof value === 'string' &&
            value in CommentPublishTypeLabelModifier
            ? CommentPublishTypeLabelModifier[value]
            : value;
        case 'readStatus':
          return value ? t('comment.unread') : null;
        case 'resolved':
          return CommentResolvedStatusLabelModifier[
            value ? 'resolved' : 'unresolved'
          ];
        case 'isCurrentActiveTabOnly': {
          return value ? t('comment.current-active-tab-only') : null;
        }
        default:
          return value;
      }
    },
    [t],
  );

  if (
    !FILTER_KEYS.includes(filterKey) ||
    (filterKey === 'isCurrentActiveTabOnly' && !value) ||
    value === undefined ||
    value === null
  ) {
    return null;
  }

  return (
    <Tag
      closable
      onClose={() => {
        onRemoveFilter(filterKey);
      }}
    >
      {getFilterLabel(filterKey, value)}
    </Tag>
  );
};
